import type { LogDto } from '../types/LogDto';
import { LS_BUFFER_KEY } from '../constants/logger';
import { get as getConfig } from './config';
import send from './send';
import { getItem, setItem, removeItem } from '../utils/local-storage';

let throttleTimeout: number | null = null;

/**
 * Sends log without throttling to the server.
 * @param logDto
 */
export function noThrottle(logDto: Array<LogDto>, applicationUid: string | null = null) {
    let buffer = [...(getItem(LS_BUFFER_KEY, applicationUid) || []), ...logDto];
    removeItem(LS_BUFFER_KEY, applicationUid);

    trySend(buffer, applicationUid);
}

/**
 * Creates an interval for sending the buffered logs to the server.
 * @param logDto
 * @param applicationUid
 */
export function throttle(logDto: Array<LogDto>, applicationUid: string | null = null) {
    const logBuffer = [...(getItem(LS_BUFFER_KEY, applicationUid) || []), ...logDto];
    setItem(LS_BUFFER_KEY, logBuffer, applicationUid);

    if (throttleTimeout) {
        return;
    }

    const throttleTime = getConfig('throttleTime', applicationUid);

    throttleTimeout = setTimeout(() => {
        throttleTimeout = null;

        let buffer = getItem(LS_BUFFER_KEY, applicationUid) || [];
        removeItem(LS_BUFFER_KEY, applicationUid);

        trySend(buffer, applicationUid);
    }, throttleTime ?? undefined);
}


function trySend(logDto: Array<LogDto>, applicationUid: string | null = null) {
    if (logDto.length === 0) return;

    const maxBatchSize = getConfig('maxBatchSize', applicationUid) ?? 0;
    while (logDto.length > maxBatchSize) {
        trySend(logDto.slice(0, maxBatchSize));
        logDto = logDto.slice(maxBatchSize);
    }

    const onFailure = () => {
        const currentBuffer = getItem(LS_BUFFER_KEY) || [];
        const newBuffer = [...currentBuffer, ...logDto];
        setItem(LS_BUFFER_KEY, newBuffer);
    };

    const logApplicationId = applicationUid || getConfig('applicationUid') as string;
    const onSendDataCallback = getConfig('onSendData', logApplicationId);
    (onSendDataCallback ?? send)(logDto, logApplicationId)
        .then(({ status, index, errors }) => {
            if (status === 200) return;

            if (status === 400 && index) {
                const invalidLog = logDto.splice(index, 1);
                console.error('LogApi responses with a bad request: an invalid log gets discarded:', {
                    log: invalidLog[0],
                    errors
                });
                trySend(logDto, applicationUid);
                return;
            } else if (status === 400) {
                console.error('LogApi responses with a bad request: all logs get discarded', logDto);
                return;
            }

            onFailure();
        })
        .catch(() => onFailure());
}
